/** Axis helpers shared by the SVG charts (presentation only). */

/** A round number at or above `v` (1, 2, 2.5, 5 × 10^n), for axis maxima. */
export function niceMax(v: number): number {
  if (!(v > 0)) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (m * exp >= v) return m * exp;
  }
  return 10 * exp;
}

/** `n + 1` evenly spaced ticks from lo to hi. */
export function ticks(lo: number, hi: number, n: number): number[] {
  return Array.from({ length: n + 1 }, (_, i) => lo + ((hi - lo) * i) / n);
}

/**
 * A readable gridline step (v44: "gridlines land on readable numbers"): the smallest of 1, 1.25,
 * 1.5, 2, 2.5, 3, 4, 5 × 10^n that covers `span` in `n` steps.
 */
export function niceStep(span: number, n = 4): number {
  const raw = span > 0 ? span / n : 1;
  const pw = Math.pow(10, Math.floor(Math.log10(Math.max(1, raw))));
  for (const k of [1, 1.25, 1.5, 2, 2.5, 3, 4, 5]) if (pw * k >= raw) return pw * k;
  return pw * 10;
}

/**
 * An axis from 0 (or below, for negative values) to at least `hi` in readable steps: the
 * gridline values, low to high. With `lo` at or above 0 it's 0 and `n` steps up.
 */
export function niceAxis(
  lo: number,
  hi: number,
  n = 4,
): { ticks: number[]; lo: number; hi: number; step: number } {
  const bottom = Math.min(0, lo);
  const step = niceStep(Math.max(hi, 0) - bottom || 1, n);
  const first = bottom < 0 ? Math.floor(bottom / step) * step : 0;
  const count = Math.max(1, Math.ceil((Math.max(hi, first + step) - first) / step - 1e-9));
  const out = Array.from({ length: count + 1 }, (_, i) => first + i * step);
  return { ticks: out, lo: first, hi: out[out.length - 1], step };
}

/**
 * A gridline's label: "$2.5k" rather than "$3k" for a $2,500 step (v44), "$1.5M", "$0". Decimals
 * appear only when the value needs them.
 */
export function tickText(v: number, step: number): string {
  if (!v) return "$0";
  const sign = v < 0 ? "−" : "";
  const a = Math.abs(v);
  const trim = (x: number) => String(+x.toFixed(2));
  if (a >= 1e6 || step >= 1e6) return `${sign}$${trim(a / 1e6)}M`;
  if (step >= 1000 || a >= 1000) return `${sign}$${trim(a / 1000)}k`;
  return `${sign}$${Math.round(a).toLocaleString("en-CA")}`;
}
